/**
 * Reports: historical review, recurring reviews and the NetSuite queue.
 *
 * The NetSuite tab sends a lead to the Formaspace middleware (formaspace.biz),
 * which creates the NetSuite lead, only when someone clicks Approve. When the key
 * is absent the tab says so plainly rather than pretending the send
 * succeeded — a dry run that looks like a real one is worse than no button.
 */

import { useCallback, useEffect, useState } from 'react';

import { api } from '../api/client';
import type {
  ImportSummary,
  NetsuiteQueueResponse,
  Report,
  ReportDetail,
} from '../api/types';
import {
  Button,
  Card,
  EmptyState,
  ErrorNotice,
  Modal,
  PageHeader,
  Spinner,
  StatusBadge,
} from '../components/ui';
import { formatDateTime, botLabel } from '../lib/format';
import { useBots } from '../hooks/useBots';

type Tab = 'historical' | 'recurring' | 'netsuite';

export function Reports(): JSX.Element {
  const { selected } = useBots();
  const slug = selected?.slug ?? null;
  const [tab, setTab] = useState<Tab>('recurring');

  if (!slug) return <EmptyState title="No bot selected" />;

  const TABS: { id: Tab; label: string }[] = [
    { id: 'historical', label: 'Historical Review' },
    { id: 'recurring', label: 'Recurring Reviews' },
    { id: 'netsuite', label: 'NetSuite Queue' },
  ];

  return (
    <div>
      <PageHeader
        title="Reports"
        description={`Lead review for ${selected ? botLabel(selected) : ''}`}
      />

      <div className="mb-4 flex gap-1 border-b border-slate-200">
        {TABS.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setTab(item.id)}
            className={`border-b-2 px-3 py-2 text-sm ${
              tab === item.id
                ? 'border-brand-blue font-medium text-brand-blue'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            {item.label}
          </button>
        ))}
      </div>

      {tab === 'historical' && <HistoricalTab slug={slug} />}
      {tab === 'recurring' && <RecurringTab slug={slug} />}
      {tab === 'netsuite' && <NetsuiteTab slug={slug} />}
    </div>
  );
}

function HistoricalTab({ slug }: { slug: string }): JSX.Element {
  const [summary, setSummary] = useState<ImportSummary | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [classified, setClassified] = useState<string | null>(null);

  async function upload(
    kind: 'conversations' | 'contacts',
    file: File | null,
  ): Promise<void> {
    if (!file) return;
    setBusy(kind);
    setError(null);
    try {
      setSummary(await api.importCsv(slug, kind, file));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Import failed');
    } finally {
      setBusy(null);
    }
  }

  async function runReview(): Promise<void> {
    setBusy('classify');
    setError(null);
    try {
      const result = await api.classifyPending(slug);
      setClassified(
        `Classified ${result.classified} conversation${
          result.classified === 1 ? '' : 's'
        }` + (result.failed ? `, ${result.failed} failed.` : '.'),
      );
      await api.runReport(slug, { type: 'historical', send_email: false });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Review failed');
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="space-y-4">
      {error && <ErrorNotice message={error} />}

      <Card className="p-5">
        <h2 className="text-base font-semibold">Import from LiveChatAI</h2>
        <p className="mt-1 text-sm text-slate-500">
          Upload the chat-inbox export and the contacts export. Column names are matched
          loosely, so the exact export format does not need to match.
        </p>

        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          {(
            [
              ['conversations', 'Chat inbox export'],
              ['contacts', 'Contacts export'],
            ] as ['conversations' | 'contacts', string][]
          ).map(([kind, label]) => (
            <div key={kind}>
              <label
                className="block text-sm font-medium text-slate-700"
                htmlFor={`upload-${kind}`}
              >
                {label}
              </label>
              <input
                id={`upload-${kind}`}
                type="file"
                accept=".csv,text/csv"
                disabled={busy !== null}
                onChange={(event) => void upload(kind, event.target.files?.[0] ?? null)}
                className="mt-1 w-full text-sm text-slate-600 file:mr-3 file:rounded-md file:border-0 file:bg-slate-100 file:px-3 file:py-1.5 file:text-sm"
              />
              {busy === kind && (
                <p className="mt-1 text-xs text-slate-500">Importing…</p>
              )}
            </div>
          ))}
        </div>

        {summary && (
          <div className="mt-4 rounded-md bg-slate-50 p-3 text-sm">
            <p className="font-medium text-slate-800">Import complete</p>
            <ul className="mt-1 space-y-0.5 text-xs text-slate-600">
              <li>
                {summary.rows_read} rows read, {summary.rows_skipped} skipped
              </li>
              <li>
                {summary.conversations_created} conversations,{' '}
                {summary.messages_created} messages
              </li>
              <li>
                {summary.contacts_created} contacts created, {summary.contacts_updated}{' '}
                updated
              </li>
            </ul>
            {summary.errors.length > 0 && (
              <ul className="mt-2 space-y-0.5 text-xs text-amber-700">
                {summary.errors.map((message, index) => (
                  <li key={index}>{message}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </Card>

      <Card className="p-5">
        <h2 className="text-base font-semibold">Run historical review</h2>
        <p className="mt-1 text-sm text-slate-500">
          Classifies every conversation that has not been reviewed yet, then generates a
          report covering all of them.
        </p>
        <div className="mt-4 flex items-center gap-3">
          <Button onClick={() => void runReview()} disabled={busy !== null}>
            {busy === 'classify' ? 'Reviewing…' : 'Run historical review'}
          </Button>
          {classified && <span className="text-sm text-slate-600">{classified}</span>}
        </div>
        <p className="mt-2 text-xs text-slate-400">
          The generated report appears under Recurring Reviews.
        </p>
      </Card>
    </div>
  );
}

function RecurringTab({ slug }: { slug: string }): JSX.Element {
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewing, setViewing] = useState<ReportDetail | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setReports((await api.listReports(slug)).items);
      setError(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not load reports');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    void load();
  }, [load]);

  async function runNow(sendEmail: boolean): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api.runReport(slug, { type: 'recurring', send_email: sendEmail });
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Run failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      {error && <ErrorNotice message={error} />}

      <Card className="flex flex-wrap items-center gap-3 p-4">
        <Button onClick={() => void runNow(false)} disabled={busy}>
          {busy ? 'Running…' : 'Run now'}
        </Button>
        <Button variant="secondary" onClick={() => void runNow(true)} disabled={busy}>
          Run and email
        </Button>
        <span className="text-xs text-slate-500">
          Runs automatically at <code>REPORT_SCHEDULE</code> in{' '}
          <code>REPORT_TIMEZONE</code> and emails <code>REPORT_RECIPIENT</code>.
        </span>
      </Card>

      <Card>
        {loading ? (
          <Spinner />
        ) : reports.length === 0 ? (
          <EmptyState
            title="No reports yet"
            hint="Run one now, or wait for the next scheduled slot."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-2 font-medium">Run</th>
                  <th className="px-4 py-2 font-medium">Type</th>
                  <th className="px-4 py-2 text-right font-medium">Reviewed</th>
                  <th className="px-4 py-2 text-right font-medium">With contact</th>
                  <th className="px-4 py-2 font-medium">Emailed</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2 font-medium" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {reports.map((report) => (
                  <tr key={report.id} className="hover:bg-slate-50">
                    <td className="px-4 py-2 text-slate-700">
                      {formatDateTime(report.run_at)}
                    </td>
                    <td className="px-4 py-2 capitalize text-slate-600">
                      {report.type}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums text-slate-600">
                      {report.stats.total_reviewed ?? 0}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums text-slate-600">
                      {report.stats.with_contact_info ?? 0}
                    </td>
                    <td className="px-4 py-2 text-xs text-slate-500">
                      {report.emailed_to ?? '—'}
                      {report.error_message && (
                        <span className="block text-amber-700">
                          {report.error_message}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      <StatusBadge status={report.status} />
                    </td>
                    <td className="px-4 py-2">
                      <button
                        type="button"
                        onClick={() =>
                          void api
                            .getReport(slug, report.id)
                            .then((detail) => setViewing(detail))
                        }
                        className="rounded px-2 py-1 text-xs text-slate-600 hover:bg-slate-100"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {viewing && (
        <Modal
          title={`Report — ${formatDateTime(viewing.run_at)}`}
          onClose={() => setViewing(null)}
          wide
        >
          {/* The body is generated by `services/reports.py`, which escapes every
              value that came from a visitor before it reaches the markup. */}
          <div
            className="max-h-[70vh] overflow-y-auto"
            dangerouslySetInnerHTML={{ __html: viewing.html_body }}
          />
        </Modal>
      )}
    </div>
  );
}

function NetsuiteTab({ slug }: { slug: string }): JSX.Element {
  const [queue, setQueue] = useState<NetsuiteQueueResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setQueue(await api.netsuiteQueue(slug));
      setError(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not load the queue');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    void load();
  }, [load]);

  async function approve(id: string): Promise<void> {
    setBusy(id);
    setError(null);
    try {
      const result = await api.approveLead(slug, id);
      setNotice(
        result.detail || (result.pushed ? 'Sent to Formaspace sales.' : 'Approved.'),
      );
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Push failed');
    } finally {
      setBusy(null);
    }
  }

  async function dismiss(id: string): Promise<void> {
    setBusy(id);
    try {
      await api.dismissLead(slug, id);
      await load();
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="space-y-4">
      {error && <ErrorNotice message={error} />}
      {notice && (
        <div className="rounded-md border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900">
          {notice}
        </div>
      )}

      {queue && !queue.netsuite_configured && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <strong>Dry-run mode.</strong> <code>FORMASPACE_API_KEY</code> is not set, so
          Approve records the decision and logs the exact lead but sends nothing to the
          Formaspace middleware. Rows stay in the queue and can be sent for real once
          the key is added.
        </div>
      )}

      {loading ? (
        <Spinner />
      ) : !queue || queue.items.length === 0 ? (
        <Card>
          <EmptyState
            title="Nothing awaiting approval"
            hint="Leads the classifier judges NetSuite-ready appear here."
          />
        </Card>
      ) : (
        queue.items.map((item) => (
          <Card key={item.classification_id} className="p-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h3 className="text-base font-semibold text-slate-900">
                  {item.company ?? 'Company not captured'}
                </h3>
                <p className="text-sm text-slate-600">
                  {item.contact_name ?? 'Name not captured'} ·{' '}
                  {item.email ?? 'no email'} · {item.phone ?? 'no phone'}
                </p>
                <p className="mt-0.5 text-xs text-slate-400">
                  {formatDateTime(item.started_at)}
                  {item.page_url ? ` · ${item.page_url}` : ''}
                </p>
              </div>
              <StatusBadge status={item.netsuite_status} />
            </div>

            <dl className="mt-3 space-y-2 text-sm">
              <div>
                <dt className="text-xs uppercase tracking-wide text-slate-400">
                  Recommendation
                </dt>
                <dd className="text-slate-700">{item.reasoning || '—'}</dd>
              </div>
              {item.excerpt && (
                <div>
                  <dt className="text-xs uppercase tracking-wide text-slate-400">
                    Chat excerpt
                  </dt>
                  <dd className="border-l-2 border-slate-200 pl-3 italic text-slate-600">
                    {item.excerpt}
                  </dd>
                </div>
              )}
            </dl>

            <div className="mt-4 flex gap-2">
              <Button
                onClick={() => void approve(item.classification_id)}
                disabled={busy === item.classification_id}
              >
                {busy === item.classification_id
                  ? 'Working…'
                  : queue.netsuite_configured
                    ? 'Approve & send to NetSuite'
                    : 'Approve (dry run)'}
              </Button>
              <Button
                variant="danger"
                onClick={() => void dismiss(item.classification_id)}
                disabled={busy === item.classification_id}
              >
                Dismiss
              </Button>
            </div>
          </Card>
        ))
      )}
    </div>
  );
}
